import { useQuery } from "@tanstack/react-query";

import { Segment } from "#app/(unauthorized)/authentication/segment";
import {
  useJwtRequiredActivities,
  useJwtSegment,
} from "#app/(unauthorized)/authentication/useJwt";
import { allQueryKeys } from "#app/_api/allQueryKeys";
import { retryFn } from "#app/lib/fetchClient.utils";
import { TimeSpan } from "#app/lib/TimeSpan";

export interface RequiredData {
  canUseFinancialPlanning: boolean;
  canUseTrust: boolean;
  hasApl: boolean;
  hasBairdResearch: boolean;
  hasStartedOnboarding: boolean;
  hasVisiblePlans: boolean;
  heldawayUserEligibility: boolean;
  heldawayUserEnrolled: boolean;
}

interface RequiredDataQueryResult {
  isPendingRequiredData: boolean;
  requiredData: RequiredData;
}

export function useRequiredData() {
  const segment = useJwtSegment();
  const requiredActivities = useJwtRequiredActivities();
  const isInstitutionalUser = segment === Segment.CorporateEntities;
  const hasRequiredActivities = requiredActivities.length > 0;

  const {
    data: dataMandatoryActivity,
    isFetched: isFetchedMandatoryActivity,
    isSuccess: isSuccessMandatoryActivity,
  } = useQuery({
    ...allQueryKeys.externalBolNotificationService.mandatoryActivity,
    staleTime: TimeSpan.fromMinutes(5).totalMilliseconds,
    notifyOnChangeProps: ["data", "isFetched", "isSuccess"],
    retry: retryFn(0),
    enabled: !hasRequiredActivities,
  });

  const {
    data: dataEligibility,
    isFetched: isFetchedEligibility,
    isSuccess: isSuccessEligibility,
  } = useQuery({
    ...allQueryKeys.externalUserProfileService.eligibility,
    staleTime: TimeSpan.fromMinutes(5).totalMilliseconds,
    notifyOnChangeProps: ["data", "isFetched", "isSuccess"],
    retry: retryFn(0),
    enabled: !hasRequiredActivities,
  });

  const { isFetched: isFetchedUserData } = useQuery({
    ...allQueryKeys.externalUserProfileService.userData,
    staleTime: TimeSpan.fromMinutes(5).totalMilliseconds,
    notifyOnChangeProps: ["data", "isFetched", "isSuccess"],
    retry: retryFn(0),
    enabled: !hasRequiredActivities,
  });

  const {
    data: dataUserRegister,
    isFetched: isFetchedUserRegister,
    isSuccess: isSuccessUserRegister,
  } = useQuery({
    ...allQueryKeys.externalHeldawayService.userRegister,
    staleTime: TimeSpan.fromMinutes(5).totalMilliseconds,
    notifyOnChangeProps: ["data", "isFetched", "isSuccess"],
    retry: retryFn(0),
    enabled: !hasRequiredActivities && !isInstitutionalUser,
  });

  const isFetchedRequiredData =
    isFetchedEligibility &&
    isFetchedMandatoryActivity &&
    isFetchedUserData &&
    (isInstitutionalUser || isFetchedUserRegister);

  return {
    isPendingRequiredData: !isFetchedRequiredData,
    requiredData: {
      canUseFinancialPlanning:
        isSuccessEligibility &&
        Boolean(dataEligibility.data.financialPlan?.canUseFinancialPlanning),

      canUseTrust:
        isSuccessEligibility && dataEligibility.data.trust?.trust !== "Hide",

      hasApl:
        isSuccessEligibility && Boolean(dataEligibility.data.apl?.isEligible),

      hasBairdResearch:
        isSuccessEligibility &&
        Boolean(dataEligibility.data.bairdResearch?.isEligible),

      hasStartedOnboarding:
        isSuccessMandatoryActivity &&
        dataMandatoryActivity.some((el) => [1, 3].includes(el.activityID)),

      hasVisiblePlans:
        isSuccessEligibility &&
        Boolean(dataEligibility.data.financialPlan?.hasVisiblePlans),

      heldawayUserEligibility:
        isSuccessEligibility &&
        Boolean(dataEligibility.data.heldaway?.isEligible),

      heldawayUserEnrolled:
        isSuccessUserRegister && Boolean(dataUserRegister?.registered),
    },
  } satisfies RequiredDataQueryResult;
}

export function useRequiredQueriesPreloader() {
  const segment = useJwtSegment();
  const requiredActivities = useJwtRequiredActivities();
  const isPreclient = segment === Segment.Preclient;
  const isCorporateEntities = segment === Segment.CorporateEntities;
  const hasRequiredActivities = requiredActivities.length > 0;

  useQuery({
    ...allQueryKeys.externalBolAccountService.sharingInvites,
    staleTime: TimeSpan.fromSeconds(5).totalMilliseconds,
    notifyOnChangeProps: [],
    retry: retryFn(1),
    enabled: !hasRequiredActivities && !isPreclient && !isCorporateEntities,
  });

  useQuery({
    ...allQueryKeys.bolAuthenticationService.messagePage,
    staleTime: TimeSpan.fromSeconds(5).totalMilliseconds,
    notifyOnChangeProps: [],
    retry: retryFn(1),
  });

  useQuery({
    ...allQueryKeys.bolAuthenticationService.messageServiceStatus,
    staleTime: TimeSpan.fromSeconds(5).totalMilliseconds,
    notifyOnChangeProps: [],
    retry: retryFn(1),
  });

  useQuery({
    ...allQueryKeys.externalBolNotificationService.outageWeb,
    staleTime: TimeSpan.fromSeconds(5).totalMilliseconds,
    notifyOnChangeProps: [],
    retry: retryFn(1),
  });
}
